import { Box, Text } from '@sendible/design-system/src';
import { styled, css } from 'styled-components';

// ── Report list page ──

export const Container = styled(Box)`
  ${({ theme: { colors, spacings } }) => css`
    width: 100%;
    min-height: 100%;
    overflow-y: auto;
    background: ${colors.brandLight};
    padding: ${spacings.s48} ${spacings.s24};
    box-sizing: border-box;
    display: block;
  `}
`;

export const Content = styled.div`
  width: 100%;
  max-width: 66.625rem;
  margin: 0 auto;
`;

// ── Section ──

export const Section = styled(Box).attrs({ semantic: 'section', orientation: 'column' })`
  ${({ theme: { spacings } }) => css`
    margin-bottom: ${spacings.s42};
    width: 100%;
  `}
`;

export const TemplateSection = styled(Box).attrs({ semantic: 'section', orientation: 'column' })`
  ${({ theme: { colors, spacings } }) => css`
    margin-bottom: ${spacings.s42};
    width: 100%;
    background: ${colors.brandDarkTint95};
    border-radius: 0.5rem;
    padding: ${spacings.s20} ${spacings.s24};
    box-sizing: border-box;
  `}
`;

export const SectionTitle = styled.h2`
  ${({ theme: { colors, fontSizes, fontWeights } }) => css`
    font-size: ${fontSizes.s16};
    font-weight: ${fontWeights.medium};
    color: ${colors.brandDark};
    margin: 0;
  `}
`;

export const SectionHeader = styled.div`
  ${({ theme: { spacings } }) => css`
    display: flex;
    align-items: center;
    gap: ${spacings.s16};
    margin-bottom: ${spacings.s16};
    width: 100%;
  `}
`;

// ── Template cards ──

export const TemplateGrid = styled.div`
  ${({ theme: { spacings } }) => css`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: ${spacings.s16};
    padding-bottom: ${spacings.s8};
  `}
`;

export const TemplateCard = styled.button<{ $isBlank?: boolean }>`
  ${({ theme: { colors, spacings, motionSpeeds, motions }, $isBlank }) => css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    min-height: 9.1875rem;
    padding: ${spacings.s24};
    border-radius: 0.5rem;
    border: 1px ${$isBlank ? 'dashed' : 'solid'} ${colors.brandDarkTint90};
    background: ${$isBlank ? `${colors.brandLight}66` : colors.brandLight};
    cursor: pointer;
    transition: all ${motionSpeeds.fast} ${motions.default};
    text-align: left;
    overflow: hidden;
    box-sizing: border-box;

    &:hover {
      border-color: ${colors.brandPrimary};
      box-shadow: 0 2px 8px ${colors.brandPrimaryAlpha20};
    }
  `}
`;

export const TemplateCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export const TemplateIcon = styled(Box).attrs({ contentAlignment: 'center', contentJustify: 'center' })`
  ${({ theme: { colors } }) => css`
    width: 1.5rem;
    height: 1.5rem;
    font-size: 1.5rem;
    color: ${colors.brandDark};
  `}
`;

export const TemplateName = styled(Text).attrs({ variation: 'body_14' })`
  ${({ theme: { fontWeights } }) => css`
    text-align: left;
    font-weight: ${fontWeights.medium};
    letter-spacing: 0.07px;
  `}
`;

export const TemplateDescription = styled(Text).attrs({ variation: 'body_12', color: 'brandDarkTint20' })`
  text-align: left;
`;

export const TemplateInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

// ── Report table ──

export const ReportTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
`;

export const ReportTableHead = styled.thead``;

export const ReportTableBody = styled.tbody``;

export const ReportTableHeader = styled.tr`
  height: 3.125rem;
`;

export const ReportTableHeaderCell = styled.th<{ $paddingLeft?: string }>`
  ${({ theme: { colors, fontSizes, motionSpeeds, spacings }, $paddingLeft = '0' }) => css`
    height: 3.125rem;
    padding: 0 0 0 ${$paddingLeft};
    font-size: ${fontSizes.s14};
    color: ${colors.brandDarkTint30};
    text-align: left;
    vertical-align: middle;
    overflow: hidden;
    transition: color ${motionSpeeds.fast};
    position: sticky;
    top: -${spacings.s48};
    z-index: 100;
    background: ${colors.brandLight};
    border-bottom: 1px solid ${colors.brandDarkTint95};

    &:hover {
      color: ${colors.brandDark};
    }
  `}
`;

export const ReportTableRow = styled.tr`
  ${({ theme: { colors, motionSpeeds, motions } }) => css`
    height: 4rem;
    border-bottom: 1px solid ${colors.brandDarkTint95};
    background: ${colors.brandLight};
    cursor: pointer;
    transition: background ${motionSpeeds.fast} ${motions.default};

    &:hover {
      background: ${colors.brandDarkTint95};
    }
  `}
`;

export const ReportTableCell = styled.td<{ $paddingLeft?: string }>`
  ${({ theme: { fontSizes, fontWeights, colors }, $paddingLeft = '0' }) => css`
    padding: 0 0 0 ${$paddingLeft};
    font-size: ${fontSizes.s14};
    font-weight: ${fontWeights.regular};
    color: ${colors.brandDarkTint20};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
  `}
`;

export const ReportTableCellName = styled(ReportTableCell)`
  ${({ theme: { colors } }) => css`
    color: ${colors.brandDark};
  `}
`;

export const ReportTableCellProfiles = styled.td`
  text-align: center;
  vertical-align: middle;
  padding: 0;
`;

export const ProfileIconsContainer = styled.div`
  ${({ theme: { spacings } }) => css`
    display: inline-flex;
    align-items: center;
    gap: ${spacings.s4};
  `}
`;

export const ProfileOverflowCount = styled.span`
  ${({ theme: { colors, fontSizes } }) => css`
    font-size: ${fontSizes.s16};
    color: ${colors.brandDarkTint30};
    white-space: nowrap;
  `}
`;

export const ReportTableCellActions = styled.td`
  text-align: center;
  vertical-align: middle;
  position: relative;
`;

export const ReportCardOptionsButton = styled.button`
  ${({ theme: { colors, borderRadius, motionSpeeds, motions } }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border: none;
    background: none;
    border-radius: ${borderRadius};
    cursor: pointer;
    color: ${colors.brandDarkAlpha50};
    transition: all ${motionSpeeds.fast} ${motions.default};
    & svg {
      height: 1.25rem;
      width: 1.25rem;
    }

    &:hover {
      background: ${colors.brandDarkTint90};
      color: ${colors.brandDark};
    }
  `}
`;

export const ReportTableCellNameButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// ── Empty state ──

export const EmptyState = styled(Box).attrs({ orientation: 'column', contentAlignment: 'center', contentJustify: 'center' })`
  ${({ theme: { spacings } }) => css`
    width: 100%;
    padding: ${spacings.s24} ${spacings.s16};
    gap: ${spacings.s24};
  `}
`;

export const EmptyStateIcon = styled.div`
  ${({ theme: { colors } }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 50%;
    background: ${colors.brandDarkTint95};
    color: ${colors.brandDark};
    font-size: 1.5rem;
  `}
`;

export const EmptyStateTitle = styled.p`
  ${({ theme: { colors, fontSizes, fontWeights } }) => css`
    font-size: ${fontSizes.s16};
    font-weight: ${fontWeights.medium};
    color: ${colors.brandDark};
    margin: 0;
  `}
`;

export const EmptyStateSubtitle = styled.p`
  ${({ theme: { colors, fontSizes } }) => css`
    font-size: ${fontSizes.s14};
    color: ${colors.brandDarkTint30};
    margin: 0;
    text-align: center;
    max-width: 18.625rem;
  `}
`;
